import type { JsonObject } from "../../../../types/bugTracker";

const STORAGE_VERSION = 2 as const;
const STORAGE_KEY_PREFIX = "hc.admin.manual-subscription-draft.v2";
const LEGACY_STORAGE_KEY_PREFIX = "hc.admin.manual-subscription-draft.v1";
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

/**
 * The last high-risk manual adjustment an operator submitted for one
 * organisation, kept so a reload or a new tab can retry the exact same
 * request against its approval without retyping the reason and details.
 */
export interface ManualSubscriptionAdjustmentDraft {
  version: typeof STORAGE_VERSION;
  actorAdminUserId: string;
  organisationId: string;
  actionType: string;
  reason: string;
  ticketUrl: string | null;
  payload: JsonObject;
  actionId: string;
  approvalRequestId: string | null;
  savedAt: string;
}

const getStorage = (provided?: Storage): Storage | null => {
  if (provided) return provided;
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

const hasActor = (actorAdminUserId: string): boolean =>
  typeof actorAdminUserId === "string" && actorAdminUserId.trim().length > 0;

const toStorageKey = (
  actorAdminUserId: string,
  organisationId: string,
): string =>
  `${STORAGE_KEY_PREFIX}.${JSON.stringify([actorAdminUserId, organisationId])}`;

const toLegacyStorageKey = (organisationId: string): string =>
  `${LEGACY_STORAGE_KEY_PREFIX}.${organisationId}`;

const isFeatureStorageKey = (key: string): boolean =>
  key.startsWith(`${STORAGE_KEY_PREFIX}.`) ||
  key.startsWith(`${LEGACY_STORAGE_KEY_PREFIX}.`);

const isDraft = (
  value: unknown,
  actorAdminUserId: string,
  organisationId: string,
): value is ManualSubscriptionAdjustmentDraft => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    record.version === STORAGE_VERSION &&
    record.actorAdminUserId === actorAdminUserId &&
    hasActor(actorAdminUserId) &&
    record.organisationId === organisationId &&
    typeof record.actionType === "string" &&
    record.actionType.length > 0 &&
    typeof record.reason === "string" &&
    record.reason.length > 0 &&
    (record.ticketUrl === null || typeof record.ticketUrl === "string") &&
    typeof record.payload === "object" &&
    record.payload !== null &&
    !Array.isArray(record.payload) &&
    typeof record.actionId === "string" &&
    UUID_PATTERN.test(record.actionId) &&
    (record.approvalRequestId === null ||
      (typeof record.approvalRequestId === "string" &&
        UUID_PATTERN.test(record.approvalRequestId))) &&
    typeof record.savedAt === "string"
  );
};

export const readManualSubscriptionAdjustmentDraft = (args: {
  actorAdminUserId: string;
  organisationId: string;
  storage?: Storage;
}): ManualSubscriptionAdjustmentDraft | null => {
  if (!hasActor(args.actorAdminUserId)) return null;
  const storage = getStorage(args.storage);
  if (!storage) return null;
  try {
    const storageKey = toStorageKey(args.actorAdminUserId, args.organisationId);
    const raw = storage.getItem(storageKey);
    const legacyStorageKey = toLegacyStorageKey(args.organisationId);
    const legacyRaw = storage.getItem(legacyStorageKey);
    if (legacyRaw !== null) {
      storage.removeItem(legacyStorageKey);
    }
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (isDraft(parsed, args.actorAdminUserId, args.organisationId)) {
      return parsed;
    }
    storage.removeItem(storageKey);
    return null;
  } catch {
    return null;
  }
};

export const writeManualSubscriptionAdjustmentDraft = (args: {
  draft: Omit<ManualSubscriptionAdjustmentDraft, "version" | "savedAt">;
  storage?: Storage;
}): ManualSubscriptionAdjustmentDraft => {
  const draft: ManualSubscriptionAdjustmentDraft = {
    ...args.draft,
    version: STORAGE_VERSION,
    savedAt: new Date().toISOString(),
  };
  if (!hasActor(draft.actorAdminUserId)) return draft;
  const storage = getStorage(args.storage);
  if (!storage) return draft;
  try {
    storage.setItem(
      toStorageKey(draft.actorAdminUserId, draft.organisationId),
      JSON.stringify(draft),
    );
    storage.removeItem(toLegacyStorageKey(draft.organisationId));
  } catch {
    // Losing the draft only costs the operator a retype; the pinned action
    // identity and the server-side approval remain the safety controls.
  }
  return draft;
};

export const clearManualSubscriptionAdjustmentDraft = (args: {
  actorAdminUserId: string;
  organisationId: string;
  storage?: Storage;
}): void => {
  if (!hasActor(args.actorAdminUserId)) return;
  const storage = getStorage(args.storage);
  if (!storage) return;
  try {
    storage.removeItem(
      toStorageKey(args.actorAdminUserId, args.organisationId),
    );
    storage.removeItem(toLegacyStorageKey(args.organisationId));
  } catch {
    // Nothing else to do: a stale draft is validated again on the next read.
  }
};

export const clearAllManualSubscriptionAdjustmentDrafts = (args?: {
  storage?: Storage;
}): void => {
  const storage = getStorage(args?.storage);
  if (!storage) return;
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && isFeatureStorageKey(key)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      storage.removeItem(key);
    }
  } catch {
    // Nothing else to do: logout must not fail because local storage is unavailable.
  }
};
